import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/common/DashboardLayout';
import AdminNav from '../../components/admin/AdminNav';
import { useAuth } from '../../context/AuthContext';
import * as adminService from '../../services/admin.service';
import AdminHubEvents from './AdminHubEvents';
import AdminMemberMigration from './AdminMemberMigration';
import AdminServerSecrets from './AdminServerSecrets';
import AdminAutomations from './AdminAutomations';
import AdminBugLog from './AdminBugLog';
import AdminClientErrors from './AdminClientErrors';
import AdminTechSpec from './AdminTechSpec';
import AdminMediaLibrary from './AdminMediaLibrary';

const SUB_TABS = [
  { key: 'health',    label: 'System Health' },
  { key: 'automations', label: 'Automations' },
  { key: 'events',    label: 'Hub Events (manual corrections)' },
  { key: 'migration', label: 'Member Migration' },
  { key: 'bugs',       label: 'Bug Log' },
  { key: 'techspec',   label: 'Tech Spec' },
  { key: 'clienterrors', label: 'Client Errors' },
  { key: 'media',      label: 'Media Library' },
];

// Server Credentials is deliberately not in the base list above — only
// added below for whoever actually has can_manage_server_secrets, same
// restriction the API route itself enforces independently.

/**
 * Not a day-to-day staff surface — this is where manual/technical
 * corrections happen: system health, matching a replay to the right
 * event, real member migration review. Jenn's own framing: "likely only
 * you, me, or Jason will go into that" — kept out of the regular Overview
 * flow so it doesn't add noise for anyone just running their day.
 */
export default function AdminBackend() {
  const { user } = useAuth();
  const subTabs = user?.can_manage_server_secrets
    ? [...SUB_TABS, { key: 'secrets', label: 'Server Credentials' }]
    : SUB_TABS;
  const [searchParams] = useSearchParams();
  // A direct link (e.g. from Video Library's "still in use" message) can
  // jump straight to a specific event instead of making Jenn find it by
  // title/date in a list mixing real events with same-named canceled
  // duplicates — a real, repeated source of confusion (2026-09-14).
  const linkedEventId = searchParams.get('eventId');
  // ?tab=automations: To-Do's from the Automations watch open straight onto that tab (2026-10-27).
  const [tab, setTab] = useState(linkedEventId ? 'events' : (searchParams.get('tab') || 'health'));
  const [health, setHealth] = useState(null);
  const [error, setError] = useState(null);

  const loadHealth = useCallback(async () => {
    try {
      setError(null);
      setHealth(await adminService.getSystemHealth());
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load system health.');
    }
  }, []);

  useEffect(() => { if (tab === 'health') loadHealth(); }, [tab, loadHealth]);

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        <AdminNav />
        <h1 className="font-serif text-2xl font-bold text-reset-brown mb-1">BackEnd</h1>
        <p className="text-sm text-gray-500 mb-5">Technical and manual-correction tools — not part of the regular daily flow.</p>

        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 text-xs font-medium w-fit mb-6">
          {subTabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded-md transition-colors ${
                tab === t.key ? 'bg-white text-reset-brown shadow-sm' : 'text-gray-500 hover:text-reset-brown'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'health' && (
          <>
            {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>}
            {!health ? (
              <p className="text-sm text-gray-400">Loading…</p>
            ) : health.ok ? (
              <div className="bg-white rounded-2xl shadow-sm px-5 py-8 text-center text-sm text-gray-400">
                Nothing needs attention right now.
              </div>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
                {health.issues.map((issue, i) => (
                  <div key={i} className="text-sm text-red-700 mb-3 last:mb-0">
                    <p>{issue.message}</p>
                    {issue.members && (
                      <ul className="mt-2 space-y-1 text-xs text-red-600 font-mono">
                        {issue.members.map(m => (
                          <li key={m.id}>
                            {m.id} — joined {new Date(m.created_at).toLocaleDateString()}
                            {m.stripe_customer_id ? ` — Stripe ${m.stripe_customer_id}` : ' — no Stripe id'}
                            {m.ghl_contact_id ? ` — GHL ${m.ghl_contact_id}` : ''}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === 'automations' && <AdminAutomations />}
        {tab === 'events' && <AdminHubEvents embedded linkedEventId={linkedEventId} />}
        {tab === 'migration' && <AdminMemberMigration embedded />}
        {tab === 'bugs' && <AdminBugLog embedded />}
        {tab === 'techspec' && <AdminTechSpec embedded />}
        {tab === 'clienterrors' && <AdminClientErrors embedded />}
        {tab === 'secrets' && <AdminServerSecrets embedded />}
        {tab === 'media' && <AdminMediaLibrary embedded />}
      </div>
    </DashboardLayout>
  );
}
